/**
 * WorldClock.ts
 * 
 * Terra Anima (테라 아니마) MMO 공용 월드 클럭 시스템
 * 
 * [공식 기준시]
 * - 현실 기준시 (EPOCH): 한국 시간 2026년 10월 23일 낮 13:00:00 (KST)
 * - 인게임 시간 환산: 1년 1월 1일 00:00:00 (솔라리아 건국기원 1년 봄의 시작)
 * 
 * [공식 시간 비율 - GAME_DESIGN.md 황금 비율 규격]
 * - 현실 48분 = 인게임 24시간 (1일 = 2,880초)
 *   * 06:00 ~ 18:00 (12시간 = 현실 24분): ☀️ 낮 (Day)
 *   * 18:00 ~ 20:00 ( 2시간 = 현실  4분): 🌅 황혼/노을 (Dusk)
 *   * 20:00 ~ 04:00 ( 8시간 = 현실 16분): 🌙 밤 (Night)
 *   * 04:00 ~ 06:00 ( 2시간 = 현실  4분): 🌄 여명/새벽 (Dawn)
 * 
 * [공식 4계절 & 1년 주기]
 * - 1계절 = 인게임 30일 (현실 24시간 = 하루)
 * - 4계절 = 봄(1~30일) ➔ 여름(31~60일) ➔ 가을(61~90일) ➔ 겨울(91~120일)
 * - 인게임 1년 = 120일 (현실 4일 = 96시간)
 */

import { SeasonType, TimeOfDay, WeatherType } from './EnvironmentSystem';

export interface WorldClockTime {
  year: number;
  season: SeasonType;
  seasonNameKr: string;
  seasonDay: number; // 1 ~ 30
  totalDays: number; // 1일부터 누적
  month: number;     // 1 ~ 12 (1계절 = 3개월, 1달 = 10일)
  day: number;       // 1 ~ 10
  hour: number;      // 0 ~ 23
  minute: number;    // 0 ~ 59
  second: number;    // 0 ~ 59
  fractionalHour: number; // 0.0 ~ 23.999... (부드러운 조도 블렌딩용 소수 시간)
  timeOfDay: TimeOfDay;
  timeOfDayLabel: string;
  weather: WeatherType;
  weatherLabel: string;
  formattedTime: string; // "봄 12일 14:25:30"
}

export class WorldClock {
  // 기준점: 2026-10-23T13:00:00+09:00 (KST)
  public static readonly EPOCH_MS = new Date('2026-10-23T13:00:00+09:00').getTime();

  // 현실 48분(2,880,000ms) = 인게임 24시간 (86,400초)
  // 배속비 = 86400 / 2880 = 30배속 (현실 1초 = 인게임 30초)
  public static readonly REAL_MS_PER_GAME_DAY = 48 * 60 * 1000; // 2,880,000 ms
  public static readonly TIME_SCALE = 30; // 1 real sec = 30 in-game sec

  // 1계절 = 30일 (현실 24시간 = 1일)
  public static readonly DAYS_PER_SEASON = 30;
  // 1년 = 4계절 = 120일 (현실 96시간 = 4일)
  public static readonly DAYS_PER_YEAR = 120;

  /**
   * 현재 실제 시각을 기준으로 테라 아니마 월드 클럭을 계산합니다.
   */
  public static now(customNowMs?: number): WorldClockTime {
    const currentMs = customNowMs ?? Date.now();
    // 경과한 현실 밀리초 (기준시 이전이면 0으로 클램프)
    const elapsedRealMs = Math.max(0, currentMs - WorldClock.EPOCH_MS);

    // 총 인게임 초 (현실 ms * 30배속 / 1000)
    const totalGameSeconds = Math.floor((elapsedRealMs * WorldClock.TIME_SCALE) / 1000);

    // 하루 = 86,400초
    const secInDay = totalGameSeconds % 86400;
    const hour = Math.floor(secInDay / 3600);
    const minute = Math.floor((secInDay % 3600) / 60);
    const second = secInDay % 60;

    // 총 누적 인게임 날수 (0일째부터 시작하므로 +1해서 1일째로 표현)
    const totalGameDays = Math.floor(totalGameSeconds / 86400);
    const year = Math.floor(totalGameDays / WorldClock.DAYS_PER_YEAR) + 1;
    const dayOfYear = totalGameDays % WorldClock.DAYS_PER_YEAR; // 0 ~ 119

    // 계절 계산 (30일씩)
    const seasonIdx = Math.floor(dayOfYear / WorldClock.DAYS_PER_SEASON); // 0: 봄, 1: 여름, 2: 가을, 3: 겨울
    const seasonDay = (dayOfYear % WorldClock.DAYS_PER_SEASON) + 1; // 1 ~ 30일

    const seasons: SeasonType[] = ['spring', 'summer', 'autumn', 'winter'];
    const season = seasons[seasonIdx];

    const seasonNames: Record<SeasonType, string> = {
      spring: '봄 (Spring)',
      summer: '여름 (Summer)',
      autumn: '가을 (Autumn)',
      winter: '겨울 (Winter)',
    };

    // 1달 = 10일, 1년 = 12달
    const month = Math.floor(dayOfYear / 10) + 1;
    const day = (dayOfYear % 10) + 1;

    // 낮/황혼/밤/여명 판정 (GAME_DESIGN.md 기준)
    // 06:00 ~ 18:00 (12시간) : day
    // 18:00 ~ 20:00 ( 2시간) : dusk
    // 20:00 ~ 04:00 ( 8시간) : night
    // 04:00 ~ 06:00 ( 2시간) : dawn
    let timeOfDay: TimeOfDay = 'day';
    let timeOfDayLabel = '☀️ 한낮';

    if (hour >= 6 && hour < 18) {
      timeOfDay = 'day';
      timeOfDayLabel = '☀️ 한낮';
    } else if (hour >= 18 && hour < 20) {
      timeOfDay = 'dusk';
      timeOfDayLabel = '🌅 노을';
    } else if (hour >= 20 || hour < 4) {
      timeOfDay = 'night';
      timeOfDayLabel = '🌙 한밤';
    } else {
      timeOfDay = 'dawn';
      timeOfDayLabel = '🌄 여명';
    }

    // 날씨(Weather) 동적 시뮬레이션
    // 인게임 3시간(현실 6분 = 10800초)마다 자연스러운 날씨 변화 시뮬레이션 (해시 기반 동기화)
    const weatherPeriodIndex = Math.floor(totalGameSeconds / 10800);
    const weatherHash = (weatherPeriodIndex * 9301 + 49297) % 233280;
    const weatherRoll = weatherHash / 233280; // 0.0 ~ 1.0

    let weather: WeatherType = 'clear';
    let weatherLabel = '🌤️ 맑음';

    if (season === 'winter') {
      // 겨울: 맑음 45%, 눈 35%, 안개 20%
      if (weatherRoll < 0.45) {
        weather = 'clear';
        weatherLabel = '🌤️ 맑음';
      } else if (weatherRoll < 0.80) {
        weather = 'snow';
        weatherLabel = '❄️ 눈';
      } else {
        weather = 'fog';
        weatherLabel = '🌫️ 안개';
      }
    } else if (season === 'summer') {
      // 여름: 맑음 60%, 뇌우 25%, 비 15%
      if (weatherRoll < 0.60) {
        weather = 'clear';
        weatherLabel = '☀️ 쾌청';
      } else if (weatherRoll < 0.85) {
        weather = 'thunderstorm';
        weatherLabel = '⛈️ 뇌우';
      } else {
        weather = 'rain';
        weatherLabel = '🌧️ 소나기';
      }
    } else if (season === 'autumn') {
      // 가을: 맑음 55%, 안개 25%, 비 20%
      if (weatherRoll < 0.55) {
        weather = 'clear';
        weatherLabel = '🌤️ 맑음';
      } else if (weatherRoll < 0.80) {
        weather = 'fog';
        weatherLabel = '🌫️ 안개';
      } else {
        weather = 'rain';
        weatherLabel = '🌧️ 비';
      }
    } else {
      // 봄: 맑음 60%, 비 25%, 안개 15%
      if (weatherRoll < 0.60) {
        weather = 'clear';
        weatherLabel = '🌤️ 맑음';
      } else if (weatherRoll < 0.85) {
        weather = 'rain';
        weatherLabel = '🌧️ 봄비';
      } else {
        weather = 'fog';
        weatherLabel = '🌫️ 안개';
      }
    }

    const hh = String(hour).padStart(2, '0');
    const mm = String(minute).padStart(2, '0');
    const ss = String(second).padStart(2, '0');
    // 사용자의 세계관 규칙: 10번의 계절 순환(10계절)을 거쳐야 비로소 1살을 먹음. "1년" 표기는 모순이므로 배제하고 계절과 일차로 표기.
    const formattedTime = `${seasonNames[season].split(' ')[0]} ${seasonDay}일 ${hh}:${mm}:${ss}`;
    const fractionalHour = hour + minute / 60 + second / 3600;

    return {
      year,
      season,
      seasonNameKr: seasonNames[season],
      seasonDay,
      totalDays: totalGameDays + 1,
      month,
      day,
      hour,
      minute,
      second,
      fractionalHour,
      timeOfDay,
      timeOfDayLabel,
      weather,
      weatherLabel,
      formattedTime,
    };
  }
}
